/**
 * SSD Intranet Usage Logger & Telemetry Engine
 * Batched, low-latency tracking of file, sheet, and document opens by user.
 * Follows modern-web-guidance for batch-analytics-events with keepalive fetch and offline caching.
 */

import type {
  UsageLogEvent,
  UsageAnalyticsData,
  UsageSourceColumn,
  UsageActionType,
} from '../types/usage';
import { getAuthToken } from '../contexts/AuthContext';
import { getUserDisplayName } from './roleUtils';

const STORAGE_KEY = 'ssd_usage_analytics_local';
const SYNC_TIMESTAMP_KEY = 'ssd_usage_last_sync';
const API_ENDPOINT = '/api/usage-logs';
const BATCH_WINDOW_MS = 1500; // 1.5 second batch debounce window
const MAX_LOCAL_EVENTS = 2500;

// BroadcastChannel for instant cross-tab real-time sync
const syncChannel =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel('ssd_usage_logs_sync')
    : null;

// In-memory cache for 0ms UI access
let cachedAnalytics: UsageAnalyticsData | null = null;
const pendingEventQueue: UsageLogEvent[] = [];
let batchTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

export interface LogFileOpenParams {
  fileId?: string;
  fileName: string;
  fileUrl?: string;
  source: UsageSourceColumn;
  action?: UsageActionType;
  category?: string;
  team?: string;
  userEmail?: string;
  userName?: string;
}

/**
 * Creates an empty analytics data object
 */
export function createEmptyAnalyticsData(): UsageAnalyticsData {
  return {
    totalOpens: 0,
    uniqueUsersCount: 0,
    uniqueFilesCount: 0,
    lastUpdatedAt: new Date().toISOString(),
    userSummaries: {},
    fileSummaries: {},
    recentEvents: [],
  };
}

/**
 * Extracts current user email and name from local auth storage
 */
export function getCurrentUserInfo(): { email: string; name: string } {
  try {
    const raw = localStorage.getItem('ssd_intranet_auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.idToken) {
        const parts = parsed.idToken.split('.');
        if (parts.length >= 2) {
          const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(decodeURIComponent(escape(atob(base64))));
          if (payload && payload.email) {
            const email = payload.email.toLowerCase().trim();
            const name = payload.name || getUserDisplayName(email) || email.split('@')[0];
            return { email, name };
          }
        }
      }
    }
  } catch {}

  return { email: 'user@ssdspc.com', name: 'Intranet User' };
}

/**
 * Normalizes file key for deduplication and grouping
 */
function normalizeFileKey(fileId?: string, fileUrl?: string, fileName?: string): string {
  if (fileId && typeof fileId === 'string' && fileId.trim().length > 0) {
    return fileId.trim().toLowerCase();
  }
  if (fileUrl && typeof fileUrl === 'string' && fileUrl.trim().length > 0) {
    return fileUrl.trim().toLowerCase().replace(/\/$/, '');
  }
  return (fileName || 'unknown-file').trim().toLowerCase();
}

/**
 * Reads local composite usage analytics from localStorage / memory
 */
export function getLocalUsageAnalytics(): UsageAnalyticsData {
  if (cachedAnalytics) {
    return cachedAnalytics;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as UsageAnalyticsData;
      if (parsed && typeof parsed === 'object') {
        cachedAnalytics = {
          ...createEmptyAnalyticsData(),
          ...parsed,
          userSummaries: parsed.userSummaries || {},
          fileSummaries: parsed.fileSummaries || {},
          recentEvents: Array.isArray(parsed.recentEvents) ? parsed.recentEvents : [],
        };
        return cachedAnalytics;
      }
    }
  } catch (err) {
    console.warn('[UsageLogger] Failed reading local usage data:', err);
  }

  cachedAnalytics = createEmptyAnalyticsData();
  return cachedAnalytics;
}

/**
 * Persists analytics locally and broadcasts to other open tabs
 */
function persistLocalAnalytics(data: UsageAnalyticsData): void {
  cachedAnalytics = data;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
  } catch (err) {
    console.warn('[UsageLogger] LocalStorage quota warning:', err);
  }

  if (syncChannel) {
    try {
      syncChannel.postMessage({ type: 'USAGE_ANALYTICS_UPDATED', payload: data });
    } catch {}
  }
}

/**
 * Updates local analytics in-place with an event
 */
function applyEventToAnalytics(data: UsageAnalyticsData, event: UsageLogEvent): void {
  const email = (event.userEmail || '').toLowerCase().trim();
  const name = event.userName || email.split('@')[0];
  const fileKey = normalizeFileKey(event.fileId, event.fileUrl, event.fileName);
  const eventTime = event.timestamp;

  // 1. User Summary
  if (!data.userSummaries[email]) {
    data.userSummaries[email] = {
      userEmail: email,
      userName: name,
      primaryTeam: event.team || '',
      totalOpens: 0,
      lastActiveAt: eventTime,
      files: {},
    };
  }

  const userRecord = data.userSummaries[email];
  userRecord.totalOpens = (userRecord.totalOpens || 0) + 1;
  userRecord.lastActiveAt = eventTime;
  if (name && name !== email.split('@')[0]) {
    userRecord.userName = name;
  }

  if (!userRecord.files[fileKey]) {
    userRecord.files[fileKey] = {
      fileId: event.fileId || fileKey,
      fileName: event.fileName,
      fileUrl: event.fileUrl,
      source: event.source,
      category: event.category || '',
      team: event.team || '',
      count: 0,
      lastOpenedAt: eventTime,
    };
  }

  const userFileRecord = userRecord.files[fileKey];
  userFileRecord.count = (userFileRecord.count || 0) + 1;
  userFileRecord.lastOpenedAt = eventTime;
  userFileRecord.fileName = event.fileName;
  if (event.fileUrl) userFileRecord.fileUrl = event.fileUrl;

  // 2. File Summary
  if (!data.fileSummaries[fileKey]) {
    data.fileSummaries[fileKey] = {
      fileId: event.fileId || fileKey,
      fileName: event.fileName,
      fileUrl: event.fileUrl,
      source: event.source,
      category: event.category || '',
      team: event.team || '',
      totalOpens: 0,
      lastOpenedAt: eventTime,
      userCounts: {},
    };
  }

  const fileRecord = data.fileSummaries[fileKey];
  fileRecord.totalOpens = (fileRecord.totalOpens || 0) + 1;
  fileRecord.lastOpenedAt = eventTime;
  fileRecord.fileName = event.fileName;
  if (event.fileUrl) fileRecord.fileUrl = event.fileUrl;

  if (!fileRecord.userCounts[email]) {
    fileRecord.userCounts[email] = {
      userEmail: email,
      userName: name,
      count: 0,
      lastOpenedAt: eventTime,
    };
  }

  const fileUserRecord = fileRecord.userCounts[email];
  fileUserRecord.count = (fileUserRecord.count || 0) + 1;
  fileUserRecord.lastOpenedAt = eventTime;
  if (name && name !== email.split('@')[0]) {
    fileUserRecord.userName = name;
  }

  // 3. Totals & Recent Events
  data.totalOpens = (data.totalOpens || 0) + 1;
  data.uniqueUsersCount = Object.keys(data.userSummaries).length;
  data.uniqueFilesCount = Object.keys(data.fileSummaries).length;
  data.lastUpdatedAt = eventTime;

  data.recentEvents = [event, ...(data.recentEvents || [])].slice(0, MAX_LOCAL_EVENTS);
}

/**
 * Flushes pending events to Cloudflare KV backend
 */
async function flushPendingEvents(): Promise<void> {
  if (batchTimeoutHandle) {
    clearTimeout(batchTimeoutHandle);
    batchTimeoutHandle = null;
  }

  if (pendingEventQueue.length === 0) return;

  const eventsToSend = [...pendingEventQueue];
  pendingEventQueue.length = 0; // Clear queue

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Use keepalive: true so requests complete even if navigation happens
    await fetch(API_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: eventsToSend }),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[UsageLogger] Background flush warning (events cached locally):', err);
  }
}

/**
 * Primary API: Log an event when a user opens any file, sheet, or document
 */
export function logFileOpen(params: LogFileOpenParams): void {
  try {
    const userInfo = getCurrentUserInfo();
    const userEmail = (params.userEmail || userInfo.email).toLowerCase().trim();
    const userName = params.userName || userInfo.name;
    const nowIso = new Date().toISOString();
    const fileId = params.fileId || normalizeFileKey(undefined, params.fileUrl, params.fileName);

    const event: UsageLogEvent = {
      id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      timestamp: nowIso,
      userEmail,
      userName,
      fileId,
      fileName: params.fileName,
      fileUrl: params.fileUrl || '',
      source: params.source,
      action: params.action || 'open_link',
      category: params.category || '',
      team: params.team || '',
    };

    // 1. Immediately apply to local in-memory & localStorage state (0ms latency)
    const current = getLocalUsageAnalytics();
    applyEventToAnalytics(current, event);
    persistLocalAnalytics(current);

    // 2. Queue for batched transmission
    pendingEventQueue.push(event);

    if (batchTimeoutHandle) {
      clearTimeout(batchTimeoutHandle);
    }
    batchTimeoutHandle = setTimeout(() => {
      void flushPendingEvents();
    }, BATCH_WINDOW_MS);
  } catch (err) {
    console.warn('[UsageLogger] Failed to log file open:', err);
  }
}

// Ensure pending events are flushed when the user navigates away or hides page
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushPendingEvents();
    }
  });

  window.addEventListener('beforeunload', () => {
    void flushPendingEvents();
  });
}

/**
 * Fetches latest usage analytics from Cloudflare KV backend
 */
export async function fetchUsageAnalyticsFromServer(): Promise<UsageAnalyticsData> {
  try {
    const res = await fetch(API_ENDPOINT);
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && json.data) {
        const serverData = json.data as UsageAnalyticsData;
        persistLocalAnalytics(serverData);
        return serverData;
      }
    }
  } catch (err) {
    console.warn('[UsageLogger] Server fetch failed, using local cache:', err);
  }

  return getLocalUsageAnalytics();
}

/**
 * Clears all usage logs from server and local storage
 */
export async function clearUsageLogsApi(): Promise<boolean> {
  try {
    const headers: HeadersInit = {};
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const res = await fetch(`${API_ENDPOINT}?action=clear`, {
      method: 'POST',
      headers,
    });

    if (res.ok) {
      const fresh = createEmptyAnalyticsData();
      persistLocalAnalytics(fresh);
      return true;
    }
  } catch (err) {
    console.warn('[UsageLogger] Failed to clear usage logs:', err);
  }

  // Fallback to local clear
  const fresh = createEmptyAnalyticsData();
  persistLocalAnalytics(fresh);
  return true;
}

/**
 * Generates and triggers CSV file download
 */
export function exportUsageToCsv(
  data: UsageAnalyticsData,
  type: 'users' | 'files' | 'events'
): void {
  let csvContent = '';
  const now = new Date().toISOString().slice(0, 10);
  let fileName = `ssd-intranet-usage-${type}-${now}.csv`;

  if (type === 'events') {
    const headers = [
      'Event ID',
      'Timestamp',
      'User Email',
      'User Name',
      'File Name',
      'Source',
      'Action',
      'File URL',
      'Category',
      'Team',
    ];
    const rows = (data.recentEvents || []).map((e) => [
      `"${(e.id || '').replace(/"/g, '""')}"`,
      `"${(e.timestamp || '').replace(/"/g, '""')}"`,
      `"${(e.userEmail || '').replace(/"/g, '""')}"`,
      `"${(e.userName || '').replace(/"/g, '""')}"`,
      `"${(e.fileName || '').replace(/"/g, '""')}"`,
      `"${(e.source || '').replace(/"/g, '""')}"`,
      `"${(e.action || 'open_link').replace(/"/g, '""')}"`,
      `"${(e.fileUrl || '').replace(/"/g, '""')}"`,
      `"${(e.category || '').replace(/"/g, '""')}"`,
      `"${(e.team || '').replace(/"/g, '""')}"`,
    ]);
    csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  } else if (type === 'files') {
    const headers = [
      'File Name',
      'Source',
      'Category',
      'Team',
      'Total Times Opened',
      'Unique Users',
      'Last Opened At',
      'File URL',
    ];
    const rows: string[] = [];
    Object.values(data.fileSummaries || {}).forEach((f) => {
      const uniqueUsers = Object.keys(f.userCounts || {}).length;
      rows.push(
        [
          `"${(f.fileName || '').replace(/"/g, '""')}"`,
          `"${(f.source || '').replace(/"/g, '""')}"`,
          `"${(f.category || '').replace(/"/g, '""')}"`,
          `"${(f.team || '').replace(/"/g, '""')}"`,
          f.totalOpens || 0,
          uniqueUsers,
          `"${(f.lastOpenedAt || '').replace(/"/g, '""')}"`,
          `"${(f.fileUrl || '').replace(/"/g, '""')}"`,
        ].join(',')
      );
    });
    csvContent = [headers.join(','), ...rows].join('\n');
  } else {
    // Default: 'users' breakdown
    const headers = [
      'User Name',
      'User Email',
      'File Name',
      'Source',
      'Times Opened By User',
      'Last Opened At',
      'File URL',
    ];
    const rows: string[] = [];
    Object.values(data.userSummaries || {}).forEach((u) => {
      Object.values(u.files || {}).forEach((f) => {
        rows.push(
          [
            `"${(u.userName || '').replace(/"/g, '""')}"`,
            `"${(u.userEmail || '').replace(/"/g, '""')}"`,
            `"${(f.fileName || '').replace(/"/g, '""')}"`,
            `"${(f.source || '').replace(/"/g, '""')}"`,
            f.count || 0,
            `"${(f.lastOpenedAt || '').replace(/"/g, '""')}"`,
            `"${(f.fileUrl || '').replace(/"/g, '""')}"`,
          ].join(',')
        );
      });
    });
    csvContent = [headers.join(','), ...rows].join('\n');
  }

  // Trigger client-side file download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Subscribes to real-time usage updates across tabs
 */
export function onUsageUpdate(callback: (data: UsageAnalyticsData) => void): () => void {
  if (!syncChannel) return () => {};

  const handler = (event: MessageEvent) => {
    if (
      event.data &&
      event.data.type === 'USAGE_ANALYTICS_UPDATED' &&
      event.data.payload
    ) {
      cachedAnalytics = event.data.payload;
      callback(event.data.payload);
    }
  };

  syncChannel.addEventListener('message', handler);
  return () => {
    syncChannel.removeEventListener('message', handler);
  };
}

/**
 * Global delegated link click tracker to catch any untagged external links
 */
let isGlobalTrackerInstalled = false;

export function initGlobalLinkTracking(): void {
  if (isGlobalTrackerInstalled || typeof window === 'undefined') return;
  isGlobalTrackerInstalled = true;

  document.addEventListener('click', (event) => {
    try {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor || !anchor.href) return;

      // If this element already has custom usage logging handled, skip to avoid double logging
      if (anchor.dataset.usageHandled === 'true') return;

      const href = anchor.href.toLowerCase();
      const isGoogleDocOrSheet =
        href.includes('docs.google.com') ||
        href.includes('drive.google.com') ||
        href.endsWith('.pdf');

      if (isGoogleDocOrSheet) {
        const title = (anchor.title || anchor.innerText || anchor.href).trim().slice(0, 150);
        logFileOpen({
          fileName: title || 'External Document',
          fileUrl: anchor.href,
          source: 'Other',
          action: 'open_link',
        });
      }
    } catch {}
  }, true);
}
